import React from 'react';
import useReactRouter from 'use-react-router';
import { gql } from '@apollo/client';
import Button from 'jbc-front/components/Button';
import { LabelMapper } from 'jbc-front/components/FormErrors';
import { BoxDouble, Section } from 'jbc-front/components/Form';
import { LoadingPage, reduxForm, useMutation, useQuery } from '../components';
import { FormSection, CheckboxField } from '../components/FieldWithDiff';
import { Buttons, Headding, Item, Lead, Panel } from '../components/PrimaryPanel';
import { CustomDescription } from '../components/QuestionDescription';
import { toFormValues } from '../utils';

const INCOME = gql`
  query income {
    request {
      id
      profile {
        id
        income {
          id
          hasBusinessIncome
          hasMiscellaneousIncome
          hasDividendIncome
          hasRealEstateIncome
          hasRetirementIncome
          hasOtherIncome
        }
      }
    }
  }
`;

const ANSWER_OTHER_INCOME_TYPE = gql`
  mutation answerOtherIncomeType($income: IncomeInput!) {
    answerOtherIncomeType(input: { income: $income }) {
      questions {
        id
        status
      }
      profile {
        id
        income {
          id
          hasBusinessIncome
          hasMiscellaneousIncome
          hasDividendIncome
          hasRealEstateIncome
          hasRetirementIncome
          hasOtherIncome
        }
      }
    }
  }
`;

const IncomeType = () => {
  return (
    <Section style={{ maxWidth: '760px' }}>
      <BoxDouble>
        <CheckboxField
          label="事業所得"
          name="hasBusinessIncome"
          note="農業、林業、水産養殖業、製造業、卸売業、小売業、金融業などのサービス業のほか対価を得て継続的に行う事業による所得"
        />
        <CheckboxField label="雑所得" name="hasMiscellaneousIncome" note="雑所得 収入金額（公的年金等に係る雑所得）" />
        <CheckboxField
          label="配当所得"
          name="hasDividendIncome"
          note="株主や出資者が法人から受ける剰余金や、利益の配当、剰余金の分配、投資法人からの金銭の分配、投資信託（公社債投資信託及び公募公社債等運用投資信託以外のもの）及び特定受益証券発行信託の収益の分配などに係る所得"
        />
        <CheckboxField
          label="不動産所得"
          name="hasRealEstateIncome"
          note="不動産の貸付けに際して受け取る権利金や頭金、更新料、名義書換料も不動産所得になります。"
        />
        <CheckboxField
          label="退職所得"
          name="hasRetirementIncome"
          note="退職手当、一時恩給その他の退職により一時に受ける給与などの所得のほか、社会保険制度等に基づく一時金など"
        />
        <CheckboxField
          label="その他所得"
          name="hasOtherIncome"
          note="⑴ 譲渡所得（土地、建物、機械、ゴルフ会員権、金地金、書画、骨とうなどの資産の譲渡による所得）
⑵ 山林所得（山林（所有期間５年超）の伐採又は譲渡による所得）
⑶ 一時所得（賞金や懸賞当せん金、競馬・競輪の払戻金（営利を目的とする継続的行為から生じたものを除きます。）、生命保険契約等に基づく一時金、損害保険契約等に基づく満期返戻金、遺失物拾得の報労金などによる所得）
⑷ 総合課税又は申告分離課税の対象となる利子所得
（注） 利子所得のうち預貯金の利子などの源泉分離課税の対象となるもの及び特定公社債の利子などの申告分離課税の対象となるもので確定申告しないことを選択したものは、収入金額に含まれません。
⑸ 申告分離課税の適用を受けた一般株式等に係る譲渡所得等又は上場株式等に係る譲渡所得等（源泉徴収選択口座を通じて行った上場株式等の譲渡による所得等で、確定申告をしないことを選択した所得等は、収入金額に含まれません。）
⑹ 先物取引に係る雑所得等 "
        />
      </BoxDouble>
    </Section>
  );
};

const QuestionForm =
  (({ handleSubmit, submitting }) => {
    return (
      <form onSubmit={handleSubmit}>
        <Lead>給与収入以外の収入の種類を選択してください。</Lead>
        <CustomDescription field="top" />
        <FormSection name="income">
          <LabelMapper name="income" label="本人の収入の" />
          <IncomeType />
        </FormSection>
        <Buttons>
          <Button primary onClick={handleSubmit} disabled={submitting}>
            次ヘ
          </Button>
        </Buttons>
      </form>
    );
  }) |> reduxForm({ form: 'otherIncomType' });

const makeInitialValues = profile => {
  const income = toFormValues(profile.income, true);

  return {
    income
  };
};

const Question = ({ dispatch }) => {
  const { loading, data } = useQuery(INCOME);
  const [answer] = useMutation(ANSWER_OTHER_INCOME_TYPE);
  const { history } = useReactRouter();
  if (loading) return <LoadingPage />;
  const {
    request: { profile }
  } = data;
  return (
    <Panel>
      <Headding>給与収入等（収入の種類）</Headding>
      <Item>
        <QuestionForm
          initialValues={makeInitialValues(profile)}
          onSubmit={async values => {
            await answer({ variables: { income: values.income } });
            history.push('/employee_input');
          }}
        />
      </Item>
    </Panel>
  );
};

export default Question;
